import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { FaFileExcel, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { formatSalesValue } from '../../../utils/formatUtils';
import { BalanceUpdateModal } from "./BalanceUpdateModal";
import LedgerGroupModal from "./LedgerGroupModal";
import LedgerClassification from "./LedgerClassification";
import TrialBalanceBranchInput from "../../SelectSearch/TrialBalanceSearchInput";

const PALETTE = [
    { bg: "bg-blue-50 border-blue-200", bgHover: "hover:border-blue-400", ring: "ring-blue-400", hoverRing: "hover:ring-blue-400" },
    { bg: "bg-red-50 border-red-200", bgHover: "hover:border-red-400", ring: "ring-red-400", hoverRing: "hover:ring-red-400" },
    { bg: "bg-purple-50 border-purple-200", bgHover: "hover:border-purple-400", ring: "ring-purple-400", hoverRing: "hover:ring-purple-400" },
    { bg: "bg-green-50 border-green-200", bgHover: "hover:border-green-400", ring: "ring-green-400", hoverRing: "hover:ring-green-400" },
    { bg: "bg-orange-50 border-orange-200", bgHover: "hover:border-orange-400", ring: "ring-orange-400", hoverRing: "hover:ring-orange-400" },
    { bg: "bg-teal-50 border-teal-200", bgHover: "hover:border-teal-400", ring: "ring-teal-400", hoverRing: "hover:ring-teal-400" },
];

const CANONICAL_ORDER = ["Asset", "Liability", "Equity", "Revenue", "Expense", "Others"];

const BASE_URL = process.env.REACT_APP_ACCOUNTING_URL;

// ── Axios instance with bearer token auth ───────────────────────────────────

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
    const token = process.env.REACT_APP_ACCOUNTING_TOKEN;
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

const parseAmount = (raw) => {
    if (raw === undefined || raw === null || raw === "") return 0;
    if (typeof raw === "number") return raw;

    const cleaned = String(raw)
        .replace(/,/g, "")
        .replace(/cr/i, "")
        .replace(/dr/i, "")
        .trim();

    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
};

const isBlank = (val) =>
    val === undefined || val === null || String(val).trim() === "";

const cleanHeader = (s) => String(s).toLowerCase().replace(/[^a-z]/g, "");

const isDebitHeader = (s) => {
    const h = cleanHeader(s);
    return h === "debit" || h === "dr" || h.endsWith("dr");
};

const isCreditHeader = (s) => {
    const h = cleanHeader(s);
    return h === "credit" || h === "cr" || h.endsWith("cr");
};

const toDebitCredit = (value) =>
    value < 0 ? { debit: 0, credit: Math.abs(value) } : { debit: value, credit: 0 };

// Normalizes a ledger name for equality comparisons (case/whitespace-insensitive)
const normalizeLedgerName = (name) => String(name || "").trim().toLowerCase();

// Combines the net (debit - credit) value of two ledgers into `target`, preserving
// totalValue bookkeeping for existing/manual ledgers whose amounts come from the backend.
const mergeLedgerAmounts = (target, source) => {
    const hasTargetTotal = target.totalValue !== undefined && target.totalValue !== null;
    const hasSourceTotal = source.totalValue !== undefined && source.totalValue !== null;

    const netTarget = hasTargetTotal ? parseFloat(target.totalValue) || 0 : target.debit - target.credit;
    const netSource = hasSourceTotal ? parseFloat(source.totalValue) || 0 : source.debit - source.credit;

    const combinedNet = netTarget + netSource;
    const { debit, credit } = toDebitCredit(combinedNet);

    return {
        ...target,
        debit,
        credit,
        ...(hasTargetTotal || hasSourceTotal ? { totalValue: combinedNet } : {}),
    };
};

const parseTrialBalance = (rows) => {
    let headerRowIndex = -1;
    const particularsCol = 0;
    let debitCol = -1;
    let creditCol = -1;

    for (let r = 0; r < rows.length; r++) {
        const row = rows[r] || [];
        const debitIdx = row.findIndex((c) => typeof c === "string" && isDebitHeader(c));
        const creditIdx = row.findIndex((c) => typeof c === "string" && isCreditHeader(c));

        if (debitIdx !== -1 && creditIdx !== -1) {
            headerRowIndex = r;
            debitCol = debitIdx;
            creditCol = creditIdx;
            break;
        }
    }

    if (headerRowIndex === -1) {
        throw new Error(
            "Could not find a header row containing Debit/Credit (or Amount (Dr.)/Amount (Cr.)) columns"
        );
    }

    const titleLines = [];
    for (let r = 0; r < headerRowIndex; r++) {
        const row = rows[r] || [];
        const line = row
            .map((c) => (c === undefined || c === null ? "" : String(c).trim()))
            .filter(Boolean)
            .join("  ");
        if (line) titleLines.push(line);
    }

    const ledgers = [];
    let grandTotal = null;

    for (let r = headerRowIndex + 1; r < rows.length; r++) {
        const row = rows[r] || [];
        const particulars = row[particularsCol];
        const debitRaw = row[debitCol];
        const creditRaw = row[creditCol];

        if (isBlank(particulars) && isBlank(debitRaw) && isBlank(creditRaw)) {
            continue;
        }

        const nameLower = String(particulars || "").trim().toLowerCase();
        const nameNormalized = nameLower.replace(/[^a-z]/g, "");

        if (nameNormalized === "total" || nameNormalized === "grandtotal") {
            grandTotal = {
                debit: parseAmount(debitRaw),
                credit: parseAmount(creditRaw),
            };
            break;
        }

        if (isBlank(particulars)) continue;

        ledgers.push({
            id: `ledger-${ledgers.length}-${nameLower.replace(/\s+/g, "-")}`,
            name: String(particulars).trim(),
            debit: parseAmount(debitRaw),
            credit: parseAmount(creditRaw),
        });
    }

    return { ledgers, grandTotal, titleLines };
};

// ── Component ────────────────────────────────────────────────────────────────

const TrialBalanceUpload = () => {
    const [fileName, setFileName] = useState("");
    const [titleLines, setTitleLines] = useState([]);
    const [ledgers, setLedgers] = useState([]);
    const [grandTotal, setGrandTotal] = useState(null);
    const [classification, setClassification] = useState({}); // { ledgerId: groupId }

    const [selectedLedgerIds, setSelectedLedgerIds] = useState(new Set());
    const [dragOverGroupId, setDragOverGroupId] = useState(null);
    const [parseError, setParseError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [accountChartGroups, setAccountChartGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);

    const [groupModalId, setGroupModalId] = useState(null);

    const [renamingLedgerId, setRenamingLedgerId] = useState(null);
    const [renameValue, setRenameValue] = useState("");

    const [addingLedger, setAddingLedger] = useState(false);
    const [newLedgerName, setNewLedgerName] = useState("");
    const [newLedgerTotal, setNewLedgerTotal] = useState(0);
    const [creatingLedger, setCreatingLedger] = useState(false);
    const [newLedgerSubLedgerDrafts, setNewLedgerSubLedgerDrafts] = useState([]);
    const [draftSubName, setDraftSubName] = useState("");
    const [draftSubTotal, setDraftSubTotal] = useState("0");

    const [expandedSubLedgerFor, setExpandedSubLedgerFor] = useState(null);
    const [addingSubLedgerFor, setAddingSubLedgerFor] = useState(null);
    const [newSubLedgerName, setNewSubLedgerName] = useState("");
    const [newSubLedgerTotal, setNewSubLedgerTotal] = useState(0);
    const [creatingSubLedger, setCreatingSubLedger] = useState(false);

    const [addingGroupForType, setAddingGroupForType] = useState(null);
    const [newGroupName, setNewGroupName] = useState("");
    const [creatingGroup, setCreatingGroup] = useState(false);

    // ── Branch selection ────────────────────────────────────────────────────

    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null); // branch id

    useEffect(() => {
        const fetchBranches = async () => {
            setLoadingBranches(true);
            try {
                const response = await api.get(`/branch/`);
                const results = response.data?.results ?? [];
                setBranches(results);
                if (results.length > 0) {
                    setSelectedBranch((prev) => prev ?? results[0].id);
                }
            } catch (error) {
                console.error(error);
                toast.error("Failed to load branches");
            } finally {
                setLoadingBranches(false);
            }
        };

        fetchBranches();
    }, []);

    // ── Fetch chart of accounts (groups + their existing ledgers/sub-ledgers) ──

    const chartFetchedRef = useRef(null);

    useEffect(() => {
        if (!selectedBranch) return;
        if (chartFetchedRef.current === selectedBranch) return;
        chartFetchedRef.current = selectedBranch;

        const fetchAccountChart = async () => {
            setLoadingGroups(true);
            try {
                const res = await api.get(`/accounting/chart-of-accounts/`, {
                    params: { branch: selectedBranch },
                });

                const groups = [];
                const existingLedgers = [];
                const existingClassification = {};

                Object.entries(res.data).forEach(([accountType, typeData]) => {
                    (typeData.groups ?? []).forEach((g) => {
                        const groupId = String(g.group_id);
                        groups.push({
                            id: groupId,
                            account_type: accountType,
                            group: g.group_name,
                        });

                        (g.ledgers ?? []).forEach((l) => {
                            const ledgerId = String(l.ledger_id);
                            const { debit, credit } = toDebitCredit(l.total_value);
                            const subLedgers = (l.sub_ledgers ?? []).map((s) => ({
                                id: String(s.sub_ledger_id),
                                name: s.name,
                                totalValue: s.value,
                                isExisting: true,
                            }));

                            existingLedgers.push({
                                id: ledgerId,
                                name: l.name,
                                debit,
                                credit,
                                totalValue: l.total_value,
                                isExisting: true,
                                subLedgers,
                            });
                            existingClassification[ledgerId] = groupId;
                        });
                    });
                });

                setAccountChartGroups(groups);

                setLedgers((prev) => {
                    const nonExisting = prev.filter((l) => !l.isExisting);
                    return [...existingLedgers, ...nonExisting];
                });
                setClassification((prev) => ({ ...existingClassification, ...prev }));
            } catch (err) {
                console.error(err);
                toast.error("Failed to load chart of accounts");
            } finally {
                setLoadingGroups(false);
            }
        };

        fetchAccountChart();
    }, [selectedBranch]);

    // Reset branch-scoped state whenever the branch changes so data doesn't bleed across branches

    const handleBranchChange = (branchId) => {
        if (branchId === selectedBranch) return;
        setSelectedBranch(branchId);
        setAccountChartGroups([]);
        setLedgers((prev) => prev.filter((l) => !l.isExisting && !l.isManual));
        setClassification({});
        setSelectedLedgerIds(new Set());
        setGroupModalId(null);
    };

    const accountTypes = useMemo(() => {
        const seen = new Set(CANONICAL_ORDER);
        accountChartGroups.forEach((g) => seen.add(g.account_type));
        const types = Array.from(seen);
        return types.sort((a, b) => {
            const ai = CANONICAL_ORDER.indexOf(a);
            const bi = CANONICAL_ORDER.indexOf(b);
            if (ai !== -1 && bi !== -1) return ai - bi;
            if (ai !== -1) return -1;
            if (bi !== -1) return 1;
            return a.localeCompare(b);
        });
    }, [accountChartGroups]);

    const colorForAccountType = useMemo(() => {
        const map = {};
        accountTypes.forEach((type, i) => {
            map[type] = PALETTE[i % PALETTE.length];
        });
        return map;
    }, [accountTypes]);

    const groupsByAccountType = useMemo(() => {
        const map = {};
        accountTypes.forEach((type) => (map[type] = []));
        accountChartGroups.forEach((g) => {
            if (!map[g.account_type]) map[g.account_type] = [];
            map[g.account_type].push(g);
        });
        return map;
    }, [accountChartGroups, accountTypes]);

    // ── Ledger classification ─────────────────────────────────────────────────

    const unclassifiedLedgers = useMemo(
        () => ledgers.filter((l) => !classification[l.id]),
        [ledgers, classification]
    );

    const ledgersByGroupId = useMemo(() => {
        const map = {};
        ledgers.forEach((l) => {
            const groupId = classification[l.id];
            if (!groupId) return;
            if (!map[groupId]) map[groupId] = [];
            map[groupId].push(l);
        });
        return map;
    }, [ledgers, classification]);

    const hasData = fileName !== "";

    const groupModal = useMemo(
        () => accountChartGroups.find((g) => g.id === groupModalId) ?? null,
        [accountChartGroups, groupModalId]
    );

    const groupModalLedgers = useMemo(
        () => (groupModalId ? ledgersByGroupId[groupModalId] ?? [] : []),
        [groupModalId, ledgersByGroupId]
    );

    const groupModalTotals = useMemo(
        () =>
            groupModalLedgers.reduce(
                (acc, l) => ({ debit: acc.debit + l.debit, credit: acc.credit + l.credit }),
                { debit: 0, credit: 0 }
            ),
        [groupModalLedgers]
    );

    const resetAll = () => {
        const keepIds = new Set(ledgers.filter((l) => l.isExisting || l.isManual).map((l) => l.id));

        setFileName("");
        setTitleLines([]);
        setLedgers((prev) => prev.filter((l) => keepIds.has(l.id)));
        setGrandTotal(null);
        setClassification((prev) => {
            const updated = {};
            Object.entries(prev).forEach(([id, groupId]) => {
                if (keepIds.has(id)) updated[id] = groupId;
            });
            return updated;
        });
        setSelectedLedgerIds(new Set());
        setParseError(null);
        setShowDetailsModal(false);
        setRenamingLedgerId(null);
        setRenameValue("");
        setGroupModalId(null);
        setAddingLedger(false);
        setNewLedgerName("");
        setNewLedgerTotal("");
        setNewLedgerSubLedgerDrafts([]);
        setDraftSubName("");
        setDraftSubTotal("");
        setExpandedSubLedgerFor(null);
        setAddingSubLedgerFor(null);
        setNewSubLedgerName("");
        setNewSubLedgerTotal("");
    };

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setParseError(null);
        setFileName(file.name);

        const persistedIds = new Set(
            ledgers.filter((l) => l.isExisting || l.isManual).map((l) => l.id)
        );

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: "",
            });

            const { ledgers: parsedLedgers, grandTotal: gt, titleLines: lines } = parseTrialBalance(rows);

            if (parsedLedgers.length === 0) {
                setParseError("No ledgers were found in this file.");
                setLedgers((prev) => prev.filter((l) => persistedIds.has(l.id)));
                setGrandTotal(null);
                setTitleLines([]);
                return;
            }

            setLedgers((prev) => [...prev.filter((l) => persistedIds.has(l.id)), ...parsedLedgers]);
            setGrandTotal(gt);
            setTitleLines(lines);
            setClassification((prev) => {
                const updated = {};
                Object.entries(prev).forEach(([id, groupId]) => {
                    if (persistedIds.has(id)) updated[id] = groupId;
                });
                return updated;
            });
            setSelectedLedgerIds(new Set());
            setGroupModalId(null);
            setShowDetailsModal(true);

            toast.success(`Parsed ${parsedLedgers.length} ledger(s) from ${file.name}`);
        } catch (err) {
            console.error(err);
            const message = err instanceof Error ? err.message : "Failed to parse the file";
            setParseError(message);
            setLedgers((prev) => prev.filter((l) => persistedIds.has(l.id)));
            setGrandTotal(null);
            setTitleLines([]);
            toast.error(message);
        }

        e.target.value = "";
    };

    // Assigns ledgers to a group. If a ledger with the same name already lives in that
    // group, the incoming ledger's amount is merged into the existing one instead of
    // creating a duplicate entry.
    const assignLedgersToGroup = useCallback(
        (ledgerIds, groupId) => {
            if (ledgerIds.length === 0) return;

            let updatedLedgers = [...ledgers];
            const updatedClassification = { ...classification };
            const idsToRemove = [];
            let mergedCount = 0;

            ledgerIds.forEach((id) => {
                const sourceLedger = updatedLedgers.find((l) => l.id === id);
                if (!sourceLedger) return;

                const sourceName = normalizeLedgerName(sourceLedger.name);

                const targetLedger = updatedLedgers.find(
                    (l) =>
                        l.id !== id &&
                        !idsToRemove.includes(l.id) &&
                        updatedClassification[l.id] === groupId &&
                        normalizeLedgerName(l.name) === sourceName
                );

                if (targetLedger) {
                    updatedLedgers = updatedLedgers.map((l) =>
                        l.id === targetLedger.id ? mergeLedgerAmounts(l, sourceLedger) : l
                    );
                    idsToRemove.push(id);
                    delete updatedClassification[id];
                    mergedCount += 1;
                } else {
                    updatedClassification[id] = groupId;
                }
            });

            updatedLedgers = updatedLedgers.filter((l) => !idsToRemove.includes(l.id));

            setLedgers(updatedLedgers);
            setClassification(updatedClassification);
            setSelectedLedgerIds(new Set());

            if (mergedCount > 0) {
                toast.success(
                    mergedCount === 1
                        ? "Merged into an existing ledger with the same name"
                        : `Merged ${mergedCount} ledger(s) into existing ledgers with the same name`
                );
            }
        },
        [ledgers, classification]
    );

    const removeLedgerFromGroup = useCallback((ledgerId) => {
        setClassification((prev) => {
            const updated = { ...prev };
            delete updated[ledgerId];
            return updated;
        });
    }, []);

    // Removes a ledger entirely from the unclassified list (and selection state).
    const handleDeleteUnclassifiedLedger = useCallback(
        (ledgerId) => {
            setLedgers((prev) => prev.filter((l) => l.id !== ledgerId));
            setSelectedLedgerIds((prev) => {
                const updated = new Set(prev);
                updated.delete(ledgerId);
                return updated;
            });
            setClassification((prev) => {
                if (!(ledgerId in prev)) return prev;
                const updated = { ...prev };
                delete updated[ledgerId];
                return updated;
            });
            if (renamingLedgerId === ledgerId) {
                setRenamingLedgerId(null);
                setRenameValue("");
            }
            toast.success("Ledger removed");
        },
        [renamingLedgerId]
    );

    // ── Drag and drop handlers ──────────────────────────────────────────────

    const handleDragStart = (e, ledgerId) => {
        const idsToDrag = selectedLedgerIds.has(ledgerId)
            ? Array.from(selectedLedgerIds)
            : [ledgerId];
        e.dataTransfer.setData("text/plain", JSON.stringify(idsToDrag));
    };

    const handleDropOnGroup = (e, groupId) => {
        e.preventDefault();
        setDragOverGroupId(null);
        const raw = e.dataTransfer.getData("text/plain");
        if (!raw) return;
        try {
            const ids = JSON.parse(raw);
            assignLedgersToGroup(ids, groupId);
        } catch {
            assignLedgersToGroup([raw], groupId);
        }
    };

    const handleDragOverGroup = (e, groupId) => {
        e.preventDefault();
        setDragOverGroupId(groupId);
    };

    // ── Click-to-select (multi) / open group modal ────────────────────────────

    const handleLedgerClick = (ledgerId) => {
        setSelectedLedgerIds((prev) => {
            const updated = new Set(prev);
            if (updated.has(ledgerId)) {
                updated.delete(ledgerId);
            } else {
                updated.add(ledgerId);
            }
            return updated;
        });
    };

    const handleGroupCardClick = (groupId) => {
        if (selectedLedgerIds.size > 0) {
            assignLedgersToGroup(Array.from(selectedLedgerIds), groupId);
            return;
        }
        setGroupModalId(groupId);
    };

    const closeGroupModal = () => {
        setGroupModalId(null);
        closeRenameInline();
        handleCancelAddLedger();
        setExpandedSubLedgerFor(null);
        closeSubLedgerForm();
    };

    // ── Inline rename ────────────────────────────────────────────────────────

    const openRenameInline = (ledgerId, currentName) => {
        setRenamingLedgerId(ledgerId);
        setRenameValue(currentName);
    };

    const closeRenameInline = () => {
        setRenamingLedgerId(null);
        setRenameValue("");
    };

    // Renaming a ledger that already belongs to a group merges it into another ledger
    // in the same group if one already has that name (amounts are added/subtracted
    // according to dr/cr into the target ledger's value).
    const handleSaveRename = () => {
        const trimmed = renameValue.trim();

        if (!trimmed) {
            toast.error("Ledger name cannot be empty");
            return;
        }

        const ledgerId = renamingLedgerId;
        const groupId = classification[ledgerId];

        if (groupId) {
            const targetLedger = ledgers.find(
                (l) =>
                    l.id !== ledgerId &&
                    classification[l.id] === groupId &&
                    normalizeLedgerName(l.name) === normalizeLedgerName(trimmed)
            );

            if (targetLedger) {
                const sourceLedger = ledgers.find((l) => l.id === ledgerId);
                const merged = mergeLedgerAmounts(targetLedger, sourceLedger);

                setLedgers((prev) =>
                    prev
                        .filter((l) => l.id !== ledgerId)
                        .map((l) => (l.id === targetLedger.id ? merged : l))
                );
                setClassification((prev) => {
                    const updated = { ...prev };
                    delete updated[ledgerId];
                    return updated;
                });

                toast.success(`Merged into existing ledger "${targetLedger.name}"`);
                closeRenameInline();
                return;
            }
        }

        setLedgers((prev) => prev.map((l) => (l.id === ledgerId ? { ...l, name: trimmed } : l)));

        toast.success("Ledger name updated");
        closeRenameInline();
    };

    const handleStartAddLedger = () => {
        setAddingLedger(true);
        setNewLedgerName("");
        setNewLedgerTotal("0");
        setNewLedgerSubLedgerDrafts([]);
        setDraftSubName("");
        setDraftSubTotal("0");
    };

    const handleCancelAddLedger = () => {
        setAddingLedger(false);
        setNewLedgerName("");
        setNewLedgerTotal("");
        setNewLedgerSubLedgerDrafts([]);
        setDraftSubName("");
        setDraftSubTotal("");
    };

    const handleAddDraftSubLedger = () => {
        const trimmed = draftSubName.trim();
        if (!trimmed) {
            toast.error("Sub-ledger name cannot be empty");
            return;
        }
        setNewLedgerSubLedgerDrafts((prev) => [
            ...prev,
            { id: `draft-${Date.now()}-${prev.length}`, name: trimmed, total: draftSubTotal },
        ]);
        setDraftSubName("");
        setDraftSubTotal("");
    };

    const handleRemoveDraftSubLedger = (id) => {
        setNewLedgerSubLedgerDrafts((prev) => prev.filter((d) => d.id !== id));
    };

    const handleSaveNewLedger = async () => {
        const trimmed = newLedgerName.trim();
        if (!trimmed) {
            toast.error("Ledger name cannot be empty");
            return;
        }
        if (!groupModalId) return;
        if (!selectedBranch) {
            toast.error("Select a branch first");
            return;
        }

        const total = parseFloat(newLedgerTotal) || 0;

        const trimmedDraftSubName = draftSubName.trim();
        const effectiveSubLedgerDrafts = trimmedDraftSubName
            ? [
                ...newLedgerSubLedgerDrafts,
                {
                    id: `draft-${Date.now()}-pending`,
                    name: trimmedDraftSubName,
                    total: draftSubTotal,
                },
            ]
            : newLedgerSubLedgerDrafts;

        const subTotalSum = effectiveSubLedgerDrafts.reduce(
            (sum, d) => sum + (parseFloat(d.total) || 0),
            0
        );

        if (
            effectiveSubLedgerDrafts.length > 0 &&
            Math.abs(subTotalSum - total) > 0.001
        ) {
            toast.error(
                subTotalSum > total
                    ? `Sub-ledger totals (${formatSalesValue(subTotalSum)}) exceed the ledger's total value (${formatSalesValue(total)})`
                    : `Sub-ledger totals (${formatSalesValue(subTotalSum)}) don't add up to the ledger's total value (${formatSalesValue(total)}). Remaining: ${formatSalesValue(total - subTotalSum)}`
            );
            return;
        }

        setCreatingLedger(true);
        try {
            if (effectiveSubLedgerDrafts.length === 0) {
                const res = await api.post(`/accountledger/create/`, {
                    ledger_name: trimmed,
                    total_value: total,
                    account_chart: Number(groupModalId),
                    branch: selectedBranch,
                });

                const created = res.data;
                const resolvedTotal = parseFloat(created.total_value) || total;
                const { debit, credit } = toDebitCredit(resolvedTotal);

                const newLedger = {
                    id: String(created.id),
                    name: created.ledger_name,
                    debit,
                    credit,
                    totalValue: resolvedTotal,
                    isExisting: true,
                    isManual: false,
                    subLedgers: [],
                };

                setLedgers((prev) => [...prev, newLedger]);
                setClassification((prev) => ({ ...prev, [newLedger.id]: groupModalId }));
            } else {
                const payload = {
                    ledger: {
                        ledger_name: trimmed,
                        account_chart: Number(groupModalId),
                        branch: selectedBranch,
                    },
                    subledgers: effectiveSubLedgerDrafts.map((d) => ({
                        sub_ledger_name: d.name,
                        total_value: parseFloat(d.total) || 0,
                    })),
                };

                const res = await api.post(`/ledger-with-subledgers/create/`, payload);

                const created = res.data;
                const resolvedTotal = parseFloat(created.ledger.total_value) || total;
                const { debit, credit } = toDebitCredit(resolvedTotal);

                const subLedgers = created.subledgers.map((s) => ({
                    id: String(s.id),
                    name: s.sub_ledger_name,
                    totalValue: parseFloat(s.total_value) || 0,
                    isExisting: true
                }));

                const newLedger = {
                    id: String(created.ledger.id),
                    name: created.ledger.ledger_name,
                    debit,
                    credit,
                    totalValue: resolvedTotal,
                    isExisting: true,
                    isManual: false,
                    subLedgers,
                };

                setLedgers((prev) => [...prev, newLedger]);
                setClassification((prev) => ({ ...prev, [newLedger.id]: groupModalId }));
            }

            toast.success("Ledger added");
            handleCancelAddLedger();
        } catch (err) {
            console.error(err);
            toast.error("Failed to create ledger");
        } finally {
            setCreatingLedger(false);
        }
    };

    const toggleSubLedgers = (ledgerId) => {
        setExpandedSubLedgerFor((prev) => (prev === ledgerId ? null : ledgerId));
        setAddingSubLedgerFor(null);
    };

    const getSubLedgerTotals = (ledger) =>
        (ledger.subLedgers ?? []).reduce((acc, s) => acc + (parseFloat(s.totalValue) || 0), 0);

    const handleStartAddSubLedger = (ledgerId) => {
        setAddingSubLedgerFor(ledgerId);
        setNewSubLedgerName("");
        setNewSubLedgerTotal("0");
    };

    const closeSubLedgerForm = () => {
        setAddingSubLedgerFor(null);
        setNewSubLedgerName("");
        setNewSubLedgerTotal("");
    };

    const handleSaveSubLedger = async (ledger) => {
        const trimmed = newSubLedgerName.trim();
        if (!trimmed) {
            toast.error("Sub-ledger name cannot be empty");
            return;
        }
        if (!selectedBranch) {
            toast.error("Select a branch first");
            return;
        }

        const total = parseFloat(newSubLedgerTotal) || 0;

        setCreatingSubLedger(true);
        try {
            const payload = {
                ledger: { id: Number(ledger.id) },
                subledgers: [{ sub_ledger_name: trimmed, total_value: total }],
                branch: selectedBranch,
            };

            const res = await api.post(`/ledger-with-subledgers/create/`, payload);

            const createdSub = res.data.subledgers[0];
            const newSub = {
                id: String(createdSub.id),
                name: createdSub.sub_ledger_name,
                totalValue: parseFloat(createdSub.total_value) || total,
            };

            setLedgers((prev) =>
                prev.map((l) => {
                    if (l.id !== ledger.id) return l;

                    const updatedTotalValue = (parseFloat(l.totalValue) || 0) + newSub.totalValue;
                    const { debit, credit } = toDebitCredit(updatedTotalValue);

                    return {
                        ...l,
                        totalValue: updatedTotalValue,
                        debit,
                        credit,
                        subLedgers: [...(l.subLedgers ?? []), newSub],
                    };
                })
            );

            toast.success("Sub-ledger added");
            closeSubLedgerForm();
        } catch (err) {
            console.error(err);
            toast.error("Failed to create sub-ledger");
        } finally {
            setCreatingSubLedger(false);
        }
    };

    // ── Add new group ────────────────────────────────────────────────────────

    const handleStartAddGroup = (accountType) => {
        setAddingGroupForType(accountType);
        setNewGroupName("");
    };

    const handleCancelAddGroup = () => {
        setAddingGroupForType(null);
        setNewGroupName("");
    };

    const handleSaveNewGroup = async () => {
        const trimmed = newGroupName.trim();
        if (!trimmed) {
            toast.error("Group name cannot be empty");
            return;
        }
        if (!addingGroupForType) return;
        if (!selectedBranch) {
            toast.error("Select a branch first");
            return;
        }

        setCreatingGroup(true);
        try {
            const res = await api.post(`/accountchart/create/`, {
                account_type: addingGroupForType,
                group: trimmed,
                is_editable: true,
                branch: selectedBranch,
            });

            const created = res.data;

            const newGroup = {
                id: String(created.id),
                account_type: created.account_type,
                group: created.group,
            };

            setAccountChartGroups((prev) => [...prev, newGroup]);
            toast.success("Group added");
            handleCancelAddGroup();
        } catch (err) {
            console.error(err);
            toast.error("Failed to create group");
        } finally {
            setCreatingGroup(false);
        }
    };

    // ── Build the bulk-import payload ───────────────────────────────────────

    const buildBulkImportPayload = useCallback(() => {
        return accountTypes
            .map((type) => ({
                account_chart: type,
                branch: selectedBranch,
                groups: (groupsByAccountType[type] ?? [])
                    .map((group) => ({
                        group: group.group,
                        ledgers: (ledgersByGroupId[group.id] ?? [])
                            .filter((l) => !l.isExisting && !l.isManual)
                            .map((l) => ({
                                ledger_name: l.name,
                                debit: l.debit,
                                credit: l.credit,
                                branch: selectedBranch
                            })),
                    }))
                    .filter((g) => g.ledgers.length > 0),
            }))
            .filter((chart) => chart.groups.length > 0);
    }, [accountTypes, groupsByAccountType, ledgersByGroupId, selectedBranch]);

    const allClassified = ledgers.length > 0 && unclassifiedLedgers.length === 0;

    const handleSaveClassification = async () => {
        if (!selectedBranch) {
            toast.error("Select a branch first");
            return;
        }

        setSaving(true);
        try {
            const payload = buildBulkImportPayload();

            await api.post(`/accountchart/bulk-import/`, payload);

            toast.success("Classification saved");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save classification");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="max-w-7xl mx-auto w-full">
                <div className="flex items-center justify-between mb-1">
                    <div className="text-lg font-semibold text-gray-800">
                        Trial Balance — Ledger Classification
                    </div>

                    <TrialBalanceBranchInput
                        branches={branches}
                        selectedBranch={selectedBranch}
                        onChange={handleBranchChange}
                        loadingBranches={loadingBranches}
                    />
                </div>

                {/* Upload + preview */}
                <div className="mb-2 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-800 rounded-md font-medium text-sm cursor-pointer hover:bg-emerald-200 transition">
                            <FaFileExcel size={14} />
                            Upload Trial Balance (.xlsx)
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                className="hidden"
                                onChange={handleFileChange}
                            />
                        </label>

                        {hasData && (
                            <div
                                onClick={() => setShowDetailsModal(true)}
                                className="flex items-center gap-4 px-3 py-2 bg-white border border-gray-200 rounded-md text-sm cursor-pointer hover:bg-gray-50 hover:shadow-sm transition"
                            >
                                <div className="flex flex-col leading-tight">
                                    <span className="font-medium text-gray-800 max-w-[220px] truncate">
                                        {fileName}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {ledgers.filter((l) => !l.isExisting).length} ledger(s)
                                        {grandTotal &&
                                            ` · Dr ${formatSalesValue(grandTotal.debit)} / Cr ${formatSalesValue(
                                                grandTotal.credit
                                            )}`}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        resetAll();
                                    }}
                                    className="p-1.5 rounded-full bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-600 transition"
                                    title="Clear uploaded data"
                                >
                                    <FaTimes size={12} />
                                </button>
                            </div>
                        )}

                        {parseError && <span className="text-sm text-red-600">{parseError}</span>}
                    </div>

                    {hasData && (
                        <button
                            type="button"
                            onClick={handleSaveClassification}
                            disabled={!allClassified || saving}
                            className="px-4 py-2 rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving
                                ? "Saving..."
                                : allClassified
                                    ? "Save Classification"
                                    : `Classify remaining ${unclassifiedLedgers.length} ledger(s)`}
                        </button>
                    )}
                </div>

                <LedgerClassification
                    hasData={hasData}
                    unclassifiedLedgers={unclassifiedLedgers}
                    selectedLedgerIds={selectedLedgerIds}
                    setSelectedLedgerIds={setSelectedLedgerIds}
                    handleLedgerClick={handleLedgerClick}
                    handleDragStart={handleDragStart}
                    loadingGroups={loadingGroups}
                    accountTypes={accountTypes}
                    colorForAccountType={colorForAccountType}
                    groupsByAccountType={groupsByAccountType}
                    ledgersByGroupId={ledgersByGroupId}
                    dragOverGroupId={dragOverGroupId}
                    setDragOverGroupId={setDragOverGroupId}
                    handleDropOnGroup={handleDropOnGroup}
                    handleDragOverGroup={handleDragOverGroup}
                    handleGroupCardClick={handleGroupCardClick}
                    addingGroupForType={addingGroupForType}
                    newGroupName={newGroupName}
                    setNewGroupName={setNewGroupName}
                    creatingGroup={creatingGroup}
                    handleSaveNewGroup={handleSaveNewGroup}
                    handleCancelAddGroup={handleCancelAddGroup}
                    handleStartAddGroup={handleStartAddGroup}
                    handleSaveClassification={handleSaveClassification}
                    allClassified={allClassified}
                    saving={saving}
                    formatSalesValue={formatSalesValue}
                    renamingLedgerId={renamingLedgerId}
                    renameValue={renameValue}
                    setRenameValue={setRenameValue}
                    openRenameInline={openRenameInline}
                    closeRenameInline={closeRenameInline}
                    handleSaveRename={handleSaveRename}
                    handleDeleteUnclassifiedLedger={handleDeleteUnclassifiedLedger}
                />
            </div>

            <BalanceUpdateModal
                showDetailsModal={showDetailsModal}
                setShowDetailsModal={setShowDetailsModal}
                titleLines={titleLines}
                ledgers={ledgers.filter((l) => !l.isExisting)}
                grandTotal={grandTotal}
                formatSalesValue={formatSalesValue}
            />

            {/* Group ledgers modal — shown on clicking a group card */}
            <LedgerGroupModal
                groupModal={groupModal}
                closeGroupModal={closeGroupModal}
                groupModalLedgers={groupModalLedgers}
                groupModalTotals={groupModalTotals}
                formatSalesValue={formatSalesValue}
                addingLedger={addingLedger}
                newLedgerName={newLedgerName}
                setNewLedgerName={setNewLedgerName}
                newLedgerTotal={newLedgerTotal}
                setNewLedgerTotal={setNewLedgerTotal}
                creatingLedger={creatingLedger}
                handleStartAddLedger={handleStartAddLedger}
                handleCancelAddLedger={handleCancelAddLedger}
                handleSaveNewLedger={handleSaveNewLedger}
                newLedgerSubLedgerDrafts={newLedgerSubLedgerDrafts}
                draftSubName={draftSubName}
                setDraftSubName={setDraftSubName}
                draftSubTotal={draftSubTotal}
                setDraftSubTotal={setDraftSubTotal}
                handleAddDraftSubLedger={handleAddDraftSubLedger}
                handleRemoveDraftSubLedger={handleRemoveDraftSubLedger}
                renamingLedgerId={renamingLedgerId}
                renameValue={renameValue}
                setRenameValue={setRenameValue}
                openRenameInline={openRenameInline}
                closeRenameInline={closeRenameInline}
                handleSaveRename={handleSaveRename}
                removeLedgerFromGroup={removeLedgerFromGroup}
                expandedSubLedgerFor={expandedSubLedgerFor}
                toggleSubLedgers={toggleSubLedgers}
                getSubLedgerTotals={getSubLedgerTotals}
                addingSubLedgerFor={addingSubLedgerFor}
                newSubLedgerName={newSubLedgerName}
                setNewSubLedgerName={setNewSubLedgerName}
                newSubLedgerTotal={newSubLedgerTotal}
                setNewSubLedgerTotal={setNewSubLedgerTotal}
                creatingSubLedger={creatingSubLedger}
                handleStartAddSubLedger={handleStartAddSubLedger}
                closeSubLedgerForm={closeSubLedgerForm}
                handleSaveSubLedger={handleSaveSubLedger}
            />
        </>
    );
};

export default TrialBalanceUpload;